import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, HelpCircle, Info, Mail, FileText, Shield, Home, MessageSquare } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "@/components/Logo";

const FAQS = [
    {
        q: "What is SRT Finder?",
        a: "SRT Finder is a dedicated online marketplace and community for high-performance Mopar vehicles — Dodge Charger SRT Hellcat, Challenger SRT Hellcat (Redeye, Widebody, Super Stock, Demon, Demon 170), Scat Pack, Chrysler 300 SRT8, and RAM performance trucks (TRX, RHO). SRT and R/T trim only — V6-equipped Chargers, Challengers, 300s, and base RAMs are not allowed.",
    },
    {
        q: "Where can I buy a Dodge Hellcat?",
        a: "You can buy a Dodge Charger SRT Hellcat or Challenger SRT Hellcat directly from private sellers and vetted dealers on SRT Finder. Every listing includes verified specs, mileage, mods list, high-resolution photos, seller ratings, and a real market-average price computed from active comparable listings.",
    },
    {
        q: "What models are allowed?",
        a: "Dodge Charger (SRT8, R/T Scat Pack, Widebody, SRT Hellcat, Hellcat Redeye, Jailbreak, Super Bee, Daytona), Dodge Challenger (R/T, Scat Pack, Widebody, SRT8, SRT Hellcat, Redeye, Super Stock, SRT Demon, Demon 170), Chrysler 300 SRT8, and RAM 1500 TRX, RHO, and Limited performance trucks.",
    },
    {
        q: "How does verification work?",
        a: "Verified sellers on SRT Finder complete a manual review by our moderation team, receive a red verified badge, and build a public reputation through post-transaction buyer ratings (1-5 stars). Buyers can filter listings by verified sellers only and review each seller's rating history before contacting them.",
    },
    {
        q: "Why not Facebook Marketplace or Craigslist?",
        a: "Unlike general marketplaces, SRT Finder is 100% dedicated to SRT and R/T Mopar performance vehicles — no V6 tire-kickers, no unrelated listings, no scams disguised as base-trim Chargers. Listings are structured (year, trim, engine, drivetrain, mileage, mods, location) so you can filter with precision.",
    },
    {
        q: "Can I sell a modified Hellcat?",
        a: "Yes. SRT Finder supports stock, mildly modified, and heavily modified vehicles. Sellers list every mod (supercharger upgrades, pulley swaps, cat-back exhaust, headers, custom tunes, built transmissions, brake kits, wheels, suspension) so buyers know exactly what they are getting.",
    },
    {
        q: "Does it support RAM TRX and RHO?",
        a: "Absolutely. RAM 1500 TRX (6.2L Supercharged HEMI, 702 hp) and RAM 1500 RHO (Hurricane 3.0L I6 High Output) are first-class citizens on SRT Finder alongside the Charger and Challenger Hellcat family.",
    },
];

export default function HamburgerMenu({ testid = "hamburger-menu" }) {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("faq");

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                data-testid={testid}
                aria-label="Open menu"
                className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                    transition: "background-color 0.15s",
                }}
            >
                <Menu className="w-5 h-5" strokeWidth={2} />
            </button>

            <AnimatePresence>
                {open && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50"
                            style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
                            onClick={() => setOpen(false)}
                        />
                        {/* Drawer from right */}
                        <motion.aside
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", damping: 30, stiffness: 260 }}
                            className="fixed top-0 right-0 h-full z-50 flex flex-col"
                            style={{
                                width: "min(420px, 92vw)",
                                background:
                                    "linear-gradient(180deg, #0a0a0a 0%, #060606 100%)",
                                borderLeft: "1px solid rgba(255,255,255,0.08)",
                                boxShadow: "-24px 0 60px rgba(0,0,0,0.6)",
                            }}
                            data-testid="hamburger-drawer"
                        >
                            {/* Red gradient wash top */}
                            <div
                                className="absolute top-0 left-0 right-0 pointer-events-none"
                                style={{
                                    height: 240,
                                    background:
                                        "radial-gradient(ellipse at 100% 0%, rgba(225,6,0,0.25) 0%, transparent 60%)",
                                }}
                            />
                            <div className="relative flex items-center justify-between px-5 pt-5 pb-4 safe-top">
                                <Logo size="sm" />
                                <button
                                    onClick={() => setOpen(false)}
                                    data-testid="hamburger-close"
                                    className="w-10 h-10 rounded-full flex items-center justify-center"
                                    style={{
                                        background: "rgba(255,255,255,0.06)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                    }}
                                    aria-label="Close menu"
                                >
                                    <X className="w-5 h-5 text-white" />
                                </button>
                            </div>

                            {/* Tab bar */}
                            <div className="relative px-5 flex gap-2 border-b border-white/5 pb-3">
                                <MenuTab
                                    active={tab === "faq"}
                                    onClick={() => setTab("faq")}
                                    icon={HelpCircle}
                                    label="FAQ"
                                    testid="tab-menu-faq"
                                />
                                <MenuTab
                                    active={tab === "about"}
                                    onClick={() => setTab("about")}
                                    icon={Info}
                                    label="About"
                                    testid="tab-menu-about"
                                />
                                <MenuTab
                                    active={tab === "contact"}
                                    onClick={() => setTab("contact")}
                                    icon={Mail}
                                    label="Contact"
                                    testid="tab-menu-contact"
                                />
                            </div>

                            <div className="relative flex-1 overflow-y-auto px-5 py-5">
                                {tab === "faq" && (
                                    <div className="space-y-3">
                                        {FAQS.map((f, i) => (
                                            <details
                                                key={i}
                                                className="rounded-2xl p-4 group"
                                                style={{
                                                    background: "rgba(255,255,255,0.03)",
                                                    border: "1px solid rgba(255,255,255,0.06)",
                                                }}
                                                data-testid={`menu-faq-${i}`}
                                            >
                                                <summary
                                                    className="cursor-pointer list-none flex items-start justify-between gap-3"
                                                    style={{
                                                        fontFamily:
                                                            "'DM Serif Display', serif",
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    <h3 className="text-white text-base leading-snug">
                                                        {f.q}
                                                    </h3>
                                                    <span className="text-onyx-500 text-xs shrink-0 mt-1 group-open:text-hellcat">
                                                        +
                                                    </span>
                                                </summary>
                                                <p className="mt-3 text-sm text-onyx-500 leading-relaxed">
                                                    {f.a}
                                                </p>
                                            </details>
                                        ))}
                                    </div>
                                )}
                                {tab === "about" && (
                                    <div className="space-y-4 text-sm text-onyx-300 leading-relaxed">
                                        <h2
                                            className="text-white text-2xl"
                                            style={{
                                                fontFamily: "'DM Serif Display', serif",
                                                fontWeight: 400,
                                            }}
                                        >
                                            About SRT Finder
                                        </h2>
                                        <p>
                                            SRT Finder is the exclusive online marketplace and
                                            community built for the Mopar performance crowd —
                                            owners, enthusiasts, and dealers of the Dodge Charger
                                            SRT Hellcat, Challenger SRT Hellcat Redeye, Widebody
                                            Scat Pack, SRT Demon and Demon 170, Chrysler 300 SRT8,
                                            and RAM TRX and RHO trucks.
                                        </p>
                                        <p>
                                            Every listing is SRT or R/T trim. No V6 Chargers, no
                                            base Challengers, no base RAMs — ever. Every seller
                                            builds a public reputation through post-transaction
                                            ratings, verified badges, and a searchable community
                                            history.
                                        </p>
                                        <p>
                                            The community is a Reddit-style forum where owners
                                            share builds, ask tech questions, plan meets, and swap
                                            parts. Because the whole platform is dedicated to one
                                            crowd, every reply is from someone who actually knows
                                            supercharged HEMI ownership.
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 pt-2">
                                            <StatCard label="Trim policy" value="SRT / R/T only" />
                                            <StatCard label="V6 policy" value="Zero. Ever." />
                                            <StatCard label="Verification" value="Manual review" />
                                            <StatCard label="Community" value="Owners only" />
                                        </div>
                                    </div>
                                )}
                                {tab === "contact" && (
                                    <div className="space-y-4">
                                        <h2
                                            className="text-white text-2xl"
                                            style={{
                                                fontFamily: "'DM Serif Display', serif",
                                                fontWeight: 400,
                                            }}
                                        >
                                            Get in touch
                                        </h2>
                                        <ContactRow
                                            icon={Mail}
                                            label="General support"
                                            href="mailto:support@srtfinder.com"
                                            value="support@srtfinder.com"
                                        />
                                        <ContactRow
                                            icon={Shield}
                                            label="Trust &amp; Safety"
                                            href="mailto:trust@srtfinder.com"
                                            value="trust@srtfinder.com"
                                        />
                                        <ContactRow
                                            icon={MessageSquare}
                                            label="Press &amp; partnerships"
                                            href="mailto:press@srtfinder.com"
                                            value="press@srtfinder.com"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Footer nav in drawer */}
                            <div className="relative px-5 py-4 border-t border-white/5 grid grid-cols-2 gap-2">
                                <DrawerLink
                                    to="/marketplace"
                                    onClose={() => setOpen(false)}
                                    icon={Home}
                                    label="Marketplace"
                                    testid="drawer-marketplace"
                                />
                                <DrawerLink
                                    to="/community"
                                    onClose={() => setOpen(false)}
                                    icon={MessageSquare}
                                    label="Community"
                                    testid="drawer-community"
                                />
                                <DrawerLink
                                    to="/the-ladder"
                                    onClose={() => setOpen(false)}
                                    icon={FileText}
                                    label="The Ladder"
                                    testid="drawer-ladder"
                                />
                                <DrawerLink
                                    to="/terms"
                                    onClose={() => setOpen(false)}
                                    icon={FileText}
                                    label="Terms"
                                    testid="drawer-terms"
                                />
                                <DrawerLink
                                    to="/privacy"
                                    onClose={() => setOpen(false)}
                                    icon={Shield}
                                    label="Privacy"
                                    testid="drawer-privacy"
                                />
                            </div>
                        </motion.aside>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}

function MenuTab({ active, onClick, icon: Icon, label, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`chip ${active ? "chip-active" : ""}`}
        >
            <Icon className="w-3.5 h-3.5" />
            {label}
        </button>
    );
}

function StatCard({ label, value }) {
    return (
        <div
            className="rounded-2xl px-4 py-3"
            style={{
                background: "rgba(225,6,0,0.06)",
                border: "1px solid rgba(225,6,0,0.2)",
            }}
        >
            <div className="label-caps">{label}</div>
            <div className="text-white text-sm font-bold mt-1">{value}</div>
        </div>
    );
}

function ContactRow({ icon: Icon, label, href, value }) {
    return (
        <a
            href={href}
            className="flex items-center gap-3 rounded-2xl px-4 py-3 hover:bg-white/5"
            style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                transition: "background-color 0.15s",
            }}
        >
            <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{
                    background: "rgba(225,6,0,0.14)",
                    border: "1px solid rgba(225,6,0,0.35)",
                }}
            >
                <Icon className="w-4 h-4 text-hellcat" />
            </div>
            <div className="min-w-0">
                <div className="label-caps">{label}</div>
                <div className="text-white text-sm font-semibold truncate">{value}</div>
            </div>
        </a>
    );
}

function DrawerLink({ to, onClose, icon: Icon, label, testid }) {
    return (
        <Link
            to={to}
            onClick={onClose}
            data-testid={testid}
            className="flex items-center gap-2 rounded-full px-4 py-2.5 text-sm text-white"
            style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                transition: "background-color 0.15s",
            }}
        >
            <Icon className="w-4 h-4" />
            <span className="font-semibold">{label}</span>
        </Link>
    );
}
